const ANALYTICS_EVENTS = Object.freeze({
  onboardingStarted: 'onboarding_started',
  onboardingStepCompleted: 'onboarding_step_completed',
  onboardingCompleted: 'onboarding_completed',
  tribeCreated: 'tribe_created',
  tribeJoined: 'tribe_joined',
  firstActivityLogged: 'first_activity_logged',
  activityLogged: 'activity_logged',
  inviteShared: 'invite_shared',
  inviteAccepted: 'invite_accepted',
  paywallViewed: 'paywall_viewed',
  purchaseStarted: 'purchase_started',
  purchaseFailed: 'purchase_failed',
  restoreCompleted: 'restore_completed',
  proValueOpened: 'pro_value_opened',
  creatorPilotActivated: 'creator_pilot_activated',
  momentumViewed: 'momentum_viewed',
  momentumActionRequested: 'momentum_action_requested',
  momentumActionResult: 'momentum_action_result',
  comebackWithin48Hours: 'comeback_within_48h',
  subscriptionTrialStarted: 'subscription_trial_started',
  subscriptionPurchased: 'subscription_purchased',
  subscriptionRenewed: 'subscription_renewed',
  subscriptionBillingIssue: 'subscription_billing_issue',
  subscriptionCancelled: 'subscription_cancelled',
  subscriptionExpired: 'subscription_expired',
});

const ANALYTICS_PARAM_KEYS = Object.freeze({
  platform: 'platform',
  appVersion: 'app_version',
  entryPoint: 'entry_point',
  plan: 'plan',
  result: 'result',
  reasonCode: 'reason_code',
  trialState: 'trial_state',
  challengeType: 'challenge_type',
  cohortKey: 'cohort_key',
});

const ALLOWED_EVENTS = new Set(Object.values(ANALYTICS_EVENTS));
const ALLOWED_PARAM_KEYS = new Set(Object.values(ANALYTICS_PARAM_KEYS));
const CODE_VALUE = /^[a-z0-9][a-z0-9_.-]{0,63}$/;
const APP_VERSION_VALUE = /^[A-Za-z0-9][A-Za-z0-9._+-]{0,31}$/;
const COHORT_KEY_VALUE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const FIREBASE_GENERATED_UID_VALUE = /^[A-Za-z0-9]{28}$/;

const ENUM_VALUES = Object.freeze({
  platform: new Set(['web', 'ios', 'android']),
  plan: new Set(['free', 'pro']),
  trial_state: new Set(['unknown', 'eligible', 'ineligible', 'active', 'converted', 'expired']),
  challenge_type: new Set(['public', 'private', 'template', 'custom', 'creator']),
});

function assertAllowedEventName(eventName) {
  if (!ALLOWED_EVENTS.has(eventName)) {
    throw new Error('Analytics event is not in the canonical allowlist.');
  }
}

function assertAllowedParam(key, value) {
  if (!ALLOWED_PARAM_KEYS.has(key)) {
    throw new Error(`Analytics parameter "${key}" is not allowed.`);
  }
  if (typeof value !== 'string') {
    throw new Error(`Analytics parameter "${key}" must be a string code.`);
  }
  if (FIREBASE_GENERATED_UID_VALUE.test(value)) {
    throw new Error(`Analytics parameter "${key}" must not contain a Firebase UID.`);
  }
  if (ENUM_VALUES[key] && !ENUM_VALUES[key].has(value)) {
    throw new Error(`Analytics parameter "${key}" has an unsupported code.`);
  }
  if (key === ANALYTICS_PARAM_KEYS.appVersion && !APP_VERSION_VALUE.test(value)) {
    throw new Error('Analytics app_version must be a non-identifying version code.');
  }
  if (key === ANALYTICS_PARAM_KEYS.cohortKey && !COHORT_KEY_VALUE.test(value)) {
    throw new Error('Analytics cohort_key must be a non-identifying cohort code.');
  }
  if (
    !ENUM_VALUES[key]
    && key !== ANALYTICS_PARAM_KEYS.appVersion
    && key !== ANALYTICS_PARAM_KEYS.cohortKey
    && !CODE_VALUE.test(value)
  ) {
    throw new Error(`Analytics parameter "${key}" must be a non-identifying code.`);
  }
}

function buildAnalyticsEvent(eventName, params = {}) {
  assertAllowedEventName(eventName);
  const normalizedParams = Object.entries(params || {}).reduce((result, [key, value]) => {
    if (value === undefined || value === null || value === '') return result;
    assertAllowedParam(key, value);
    return {
      ...result,
      [key]: value,
    };
  }, {});

  return Object.freeze({
    name: eventName,
    params: Object.freeze(normalizedParams),
  });
}

function createAnalyticsLogger({
  appVersion,
  logEvent,
  platform,
} = {}) {
  if (typeof logEvent !== 'function') {
    throw new Error('A canonical analytics logger requires a logEvent function.');
  }
  return (eventName, params = {}) => {
    const event = buildAnalyticsEvent(eventName, {
      platform,
      app_version: appVersion,
      ...params,
    });
    logEvent(event.name, event.params);
    return event;
  };
}

export {
  ANALYTICS_EVENTS,
  ANALYTICS_PARAM_KEYS,
  buildAnalyticsEvent,
  createAnalyticsLogger,
};
